import { useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, ArrowUp, X, Loader2, FileText, Video, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const ASPECTS = [
  { id: "16:9", label: "Landscape" },
  { id: "9:16", label: "Portrait" },
  { id: "1:1", label: "Square" },
];
const LENGTHS = [
  { id: "short", label: "Short · ~3s" },
  { id: "medium", label: "Medium · ~5s" },
  { id: "long", label: "Long · ~8s" },
];

export const ChatInput = ({ onSend, busy }) => {
  const [text, setText] = useState("");
  const [images, setImages] = useState([]); // data urls
  const [docs, setDocs] = useState([]); // {doc_id, filename}
  const [uploading, setUploading] = useState(false);
  const [videoOpts, setVideoOpts] = useState({ aspect_ratio: "16:9", length: "medium" });
  const fileRef = useRef(null);
  const taRef = useRef(null);

  const handleFiles = async (files) => {
    for (const file of Array.from(files)) {
      const isImage = file.type.startsWith("image/");
      const isDoc = /\.(pdf|csv|txt|md)$/i.test(file.name);
      if (isImage) {
        if (file.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4MB)."); continue; }
        const reader = new FileReader();
        reader.onload = () => setImages((prev) => [...prev, reader.result]);
        reader.readAsDataURL(file);
      } else if (isDoc) {
        if (file.size > 15 * 1024 * 1024) { toast.error("File too large (max 15MB)."); continue; }
        setUploading(true);
        try {
          const form = new FormData();
          form.append("file", file);
          const { data } = await api.post("/upload/document", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setDocs((prev) => [...prev, { doc_id: data.doc_id, filename: data.filename }]);
          toast.success(`Attached ${data.filename}`);
        } catch (err) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
        } finally {
          setUploading(false);
        }
      } else {
        toast.error("Unsupported file. Use an image, PDF, CSV, TXT or MD.");
      }
    }
  };

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed && images.length === 0 && docs.length === 0) return;
    if (busy || uploading) return;
    onSend(
      trimmed || (docs.length ? "Summarize the attached document." : "(image)"),
      images,
      docs.map((d) => d.doc_id),
      videoOpts,
      { docNames: docs.map((d) => d.filename) }
    );
    setText("");
    setImages([]);
    setDocs([]);
    if (taRef.current) taRef.current.style.height = "auto";
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const autosize = (e) => {
    setText(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-4">
      <div className="rounded-2xl border border-white/10 bg-[#141415] backdrop-blur-xl focus-within:border-[#00FF66]/40 transition-colors">
        {(images.length > 0 || docs.length > 0) && (
          <div className="flex flex-wrap gap-2 p-3 pb-0">
            {images.map((img, i) => (
              <div key={`img-${i}`} className="relative group">
                <img src={img} alt="preview" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
                <button
                  data-testid={`remove-image-${i}`}
                  onClick={() => setImages(images.filter((_, idx) => idx !== i))}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black border border-white/20 flex items-center justify-center hover:bg-red-500 transition-colors"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            {docs.map((d, i) => (
              <div
                key={`doc-${i}`}
                data-testid={`doc-chip-${i}`}
                className="relative group flex items-center gap-2 pl-2.5 pr-7 py-2 rounded-lg border border-white/10 bg-[#0A0A0A] max-w-[220px]"
              >
                <FileText className="w-4 h-4 text-[#00FF66] shrink-0" />
                <span className="text-xs text-zinc-300 truncate">{d.filename}</span>
                <button
                  data-testid={`remove-doc-${i}`}
                  onClick={() => setDocs(docs.filter((_, idx) => idx !== i))}
                  className="absolute top-1/2 -translate-y-1/2 right-1.5 w-5 h-5 rounded-full bg-black border border-white/20 flex items-center justify-center hover:bg-red-500 transition-colors"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2 p-3">
          <button
            data-testid="attach-file-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title="Attach image, PDF, CSV, TXT or MD"
            className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-zinc-400 hover:text-[#00FF66] hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
          </button>

          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="video-options-btn"
                title="Video options"
                className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-zinc-400 hover:text-[#00FF66] hover:bg-white/5 transition-colors"
              >
                <Video className="w-5 h-5" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              data-testid="video-options-popover"
              className="w-64 bg-[#141415] border-white/10 text-white"
              align="start"
            >
              <p className="text-sm font-semibold mb-1">Video generation</p>
              <p className="text-xs text-zinc-500 mb-3">Used when you ask NIGAMAI to make a video.</p>
              <div className="mb-3">
                <p className="text-xs text-zinc-400 mb-1.5">Aspect ratio</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {ASPECTS.map((a) => (
                    <button
                      key={a.id}
                      data-testid={`aspect-${a.id.replace(":", "-")}`}
                      onClick={() => setVideoOpts((o) => ({ ...o, aspect_ratio: a.id }))}
                      className={`text-xs py-1.5 rounded-md border transition-colors ${
                        videoOpts.aspect_ratio === a.id
                          ? "border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66]"
                          : "border-white/10 text-zinc-400 hover:border-white/25"
                      }`}
                    >
                      {a.id}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-zinc-400 mb-1.5">Length</p>
                <div className="space-y-1.5">
                  {LENGTHS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`length-${l.id}`}
                      onClick={() => setVideoOpts((o) => ({ ...o, length: l.id }))}
                      className={`w-full flex items-center justify-between text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                        videoOpts.length === l.id
                          ? "border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66]"
                          : "border-white/10 text-zinc-400 hover:border-white/25"
                      }`}
                    >
                      {l.label}
                      {videoOpts.length === l.id && <Check className="w-3.5 h-3.5" />}
                    </button>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf,.csv,.txt,.md"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <Textarea
            ref={taRef}
            data-testid="chat-input-textarea"
            value={text}
            onChange={autosize}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder="Ask NIGAMAI to build anything, or attach a PDF/CSV to ask about it..."
            className="flex-1 resize-none border-0 bg-transparent text-white placeholder:text-zinc-500 focus-visible:ring-0 focus-visible:ring-offset-0 min-h-0 py-2 max-h-[200px]"
          />
          <button
            data-testid="send-message-btn"
            onClick={submit}
            disabled={busy || uploading || (!text.trim() && images.length === 0 && docs.length === 0)}
            className="shrink-0 w-9 h-9 rounded-lg bg-[#00FF66] hover:bg-[#00CC52] disabled:opacity-30 disabled:hover:bg-[#00FF66] flex items-center justify-center transition-colors"
          >
            {busy ? (
              <Loader2 className="w-5 h-5 text-black animate-spin" />
            ) : (
              <ArrowUp className="w-5 h-5 text-black" />
            )}
          </button>
        </div>
      </div>
      <p className="text-center text-xs text-zinc-600 mt-2">
        NIGAMAI can make mistakes. Verify important code.
      </p>
    </div>
  );
};
